import { Component, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../../icons/icon.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * Validation Rule
 */
export interface ValidationRule {
  type: 'required' | 'minLength' | 'maxLength' | 'pattern' | 'email' | 'custom';
  message: string;
  value?: any;
  validator?: (value: any) => boolean;
}

/**
 * Form Field Component
 * 
 * Reusable form input field with validation, error messages, and accessibility.
 * Supports text, email, password, number, tel, url, and textarea types.
 * 
 * @example
 * ```typescript
 * // In your component
 * nameValue = signal('');
 * 
 * validationRules: ValidationRule[] = [
 *   { type: 'required', message: 'Nome é obrigatório' },
 *   { type: 'minLength', value: 3, message: 'Mínimo 3 caracteres' }
 * ];
 * ```
 * 
 * ```html
 * <mg3p-form-field
 *   label="Nome Completo"
 *   [value]="nameValue()"
 *   (valueChange)="nameValue.set($event)"
 *   [validationRules]="validationRules"
 *   [required]="true"
 *   helpText="Digite seu nome completo"
 * />
 * ```
 * 
 * Features:
 * - Multiple input types
 * - Real-time validation
 * - Error messages
 * - Help text
 * - Character counter
 * - Prefix/suffix icons
 * - Disabled state
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-form-field',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div 
      class="form-field"
      [class.form-field--error]="showError()"
      [class.form-field--disabled]="disabled()"
      [class.form-field--focused]="isFocused()"
    >
      <!-- Label -->
      @if (label()) {
        <label 
          [for]="fieldId()"
          class="form-field__label"
        >
          {{ label() }}
          @if (required()) {
            <span class="form-field__required" aria-label="obrigatório">*</span>
          }
        </label>
      }

      <!-- Input wrapper -->
      <div class="form-field__input-wrapper">
        @if (prefixIconName()) {
          <span class="form-field__prefix" aria-hidden="true">
            <mg3p-icon [name]="prefixIconName()!" size="sm" />
          </span>
        } @else if (prefixIcon()) {
          <span class="form-field__prefix" aria-hidden="true">{{ prefixIcon() }}</span>
        }

        @if (type() === 'textarea') {
          <!-- Textarea -->
          <textarea
            [id]="fieldId()"
            class="form-field__input form-field__textarea"
            [value]="value()"
            (input)="onInput($event)"
            (focus)="onFocus()"
            (blur)="onBlur()"
            [placeholder]="placeholder()"
            [disabled]="disabled()"
            [required]="required()"
            [rows]="rows()"
            [attr.maxlength]="maxLength() || undefined"
            [attr.aria-invalid]="showError()"
            [attr.aria-describedby]="getAriaDescribedBy()"
          ></textarea>
        } @else {
          <!-- Input -->
          <input
            [id]="fieldId()"
            [type]="type()"
            class="form-field__input"
            [value]="value()"
            (input)="onInput($event)"
            (focus)="onFocus()"
            (blur)="onBlur()"
            [placeholder]="placeholder()"
            [disabled]="disabled()"
            [required]="required()"
            [attr.maxlength]="maxLength() || undefined"
            [attr.aria-invalid]="showError()"
            [attr.aria-describedby]="getAriaDescribedBy()"
            autocomplete="off"
          />
        }

        @if (suffixIcon()) {
          <span class="form-field__suffix" aria-hidden="true">{{ suffixIcon() }}</span>
        }

        @if (showClearButton()) {
          <button
            type="button"
            class="form-field__clear"
            (click)="clearValue()"
            [attr.aria-label]="'Limpar ' + label()"
          >
            <span aria-hidden="true">✕</span>
          </button>
        }
      </div>

      <!-- Help text / Error message / Character counter -->
      <div class="form-field__footer">
        <div class="form-field__messages">
          @if (showError() && errorMessage()) {
            <div 
              [id]="fieldId() + '-error'"
              class="form-field__error-message"
              role="alert"
            >
              <span aria-hidden="true">⚠️</span>
              {{ errorMessage() }}
            </div>
          } @else if (helpText()) {
            <div 
              [id]="fieldId() + '-help'"
              class="form-field__help-text"
            >
              {{ helpText() }}
            </div>
          }
        </div>

        @if (showCharacterCount()) {
          <div class="form-field__counter">
            {{ value().length }}@if (maxLength()) { / {{ maxLength() }} }
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .form-field {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    .form-field__label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
    }

    .form-field__required {
      color: var(--color-danger);
      margin-left: var(--spacing-xs);
    }

    .form-field__input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
    }

    .form-field__input,
    .form-field__textarea {
      flex: 1;
      padding: var(--spacing-sm) var(--spacing-md);
      font-size: var(--font-size-md);
      color: var(--color-text-primary);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      transition: all var(--transition-fast);
      outline: none;
    }

    .form-field__input::placeholder,
    .form-field__textarea::placeholder {
      color: var(--color-text-tertiary);
    }

    .form-field__input:hover:not(:disabled),
    .form-field__textarea:hover:not(:disabled) {
      border-color: var(--color-border-hover);
    }

    .form-field__input:focus,
    .form-field__textarea:focus {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px var(--color-primary-light);
    }

    .form-field__input:disabled,
    .form-field__textarea:disabled {
      background: var(--color-surface-secondary);
      color: var(--color-text-tertiary);
      cursor: not-allowed;
    }

    .form-field__textarea {
      resize: vertical;
      min-height: 80px;
      font-family: inherit;
      line-height: 1.5;
    }

    .form-field__prefix,
    .form-field__suffix {
      position: absolute;
      font-size: var(--font-size-lg);
      color: var(--color-text-tertiary);
      pointer-events: none;
    }

    .form-field__prefix {
      left: var(--spacing-md);
    }

    .form-field__suffix {
      right: var(--spacing-md);
    }

    .form-field__input-wrapper:has(.form-field__prefix) .form-field__input {
      padding-left: calc(var(--spacing-md) * 2 + var(--font-size-lg));
    }

    .form-field__input-wrapper:has(.form-field__suffix) .form-field__input {
      padding-right: calc(var(--spacing-md) * 2 + var(--font-size-lg));
    }

    .form-field__clear {
      position: absolute;
      right: var(--spacing-sm);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: var(--radius-sm);
      color: var(--color-text-tertiary);
      cursor: pointer;
      transition: all var(--transition-fast);
    }

    .form-field__clear:hover {
      background: var(--color-surface-hover);
      color: var(--color-text-primary);
    }

    .form-field__clear:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .form-field__footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: var(--spacing-sm);
      min-height: 20px;
    }

    .form-field__messages {
      flex: 1;
    }

    .form-field__help-text {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
    }

    .form-field__error-message {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-size: var(--font-size-sm);
      color: var(--color-danger);
    }

    .form-field__counter {
      font-size: var(--font-size-sm);
      color: var(--color-text-tertiary);
      white-space: nowrap;
    }

    /* Error state */
    .form-field--error .form-field__input,
    .form-field--error .form-field__textarea {
      border-color: var(--color-danger);
    }

    .form-field--error .form-field__input:focus,
    .form-field--error .form-field__textarea:focus {
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }

    /* Disabled state */
    .form-field--disabled {
      opacity: 0.6;
    }

    /* Focused state */
    .form-field--focused .form-field__label {
      color: var(--color-primary);
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .form-field__input,
      .form-field__textarea,
      .form-field__clear {
        transition: none;
      }
    }
  `]
})
export class FormFieldComponent {
  // Inputs
  label = input<string>('');
  value = input<string>('');
  type = input<'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'textarea'>('text');
  placeholder = input<string>('');
  helpText = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  maxLength = input<number | null>(null);
  rows = input<number>(4);
  prefixIcon = input<string>('');
  prefixIconName = input<NavIconName | undefined>(undefined);
  suffixIcon = input<string>('');
  showCharacterCount = input<boolean>(false);
  clearable = input<boolean>(false);
  validationRules = input<ValidationRule[]>([]);
  validateOnBlur = input<boolean>(true);
  validateOnChange = input<boolean>(false);

  // Outputs
  valueChange = output<string>();
  validationChange = output<boolean>();

  // State
  isFocused = signal<boolean>(false);
  isTouched = signal<boolean>(false);
  errorMessage = signal<string>('');
  fieldId = signal<string>(`form-field-${Math.random().toString(36).substr(2, 9)}`);

  // Computed
  showError = computed(() => {
    return this.isTouched() && !!this.errorMessage();
  });

  showClearButton = computed(() => {
    return this.clearable() && this.value().length > 0 && !this.disabled();
  });

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const newValue = input.value;
    
    this.valueChange.emit(newValue);

    if (this.validateOnChange()) {
      this.validate(newValue);
    }
  }

  onFocus(): void {
    this.isFocused.set(true);
  }

  onBlur(): void {
    this.isFocused.set(false);
    this.isTouched.set(true);

    if (this.validateOnBlur()) {
      this.validate(this.value());
    }
  }

  clearValue(): void {
    this.valueChange.emit('');
    this.errorMessage.set('');
    this.validationChange.emit(true);
  }

  validate(value: string): boolean {
    const rules = this.validationRules();

    // Check required
    if (this.required() && !value.trim()) {
      const requiredRule = rules.find(r => r.type === 'required');
      this.errorMessage.set(requiredRule?.message || 'Campo obrigatório');
      this.validationChange.emit(false);
      return false;
    }

    // Check other rules
    for (const rule of rules) {
      if (!this.validateRule(value, rule)) {
        this.errorMessage.set(rule.message);
        this.validationChange.emit(false);
        return false;
      }
    }

    this.errorMessage.set('');
    this.validationChange.emit(true);
    return true;
  }

  private validateRule(value: string, rule: ValidationRule): boolean {
    if (!value) return true; // Skip validation if empty (unless required)

    switch (rule.type) {
      case 'required':
        return value.trim().length > 0;

      case 'minLength':
        return value.length >= (rule.value || 0);

      case 'maxLength':
        return value.length <= (rule.value || Infinity);

      case 'pattern':
        return new RegExp(rule.value).test(value);

      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

      case 'custom':
        return rule.validator ? rule.validator(value) : true;

      default:
        return true;
    }
  }

  getAriaDescribedBy(): string {
    const ids: string[] = [];
    
    if (this.showError() && this.errorMessage()) {
      ids.push(`${this.fieldId()}-error`);
    } else if (this.helpText()) {
      ids.push(`${this.fieldId()}-help`);
    }

    return ids.join(' ') || undefined as any;
  }

  // Public method to trigger validation
  public triggerValidation(): boolean {
    this.isTouched.set(true);
    return this.validate(this.value());
  }
}

// Made with Bob
